import {
  DataGrid,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import { Box, IconButton } from '@mui/material'

import { t } from 'i18next'
import { useEffect, useState, useCallback } from 'react'
import { add, find, noop } from 'es-toolkit/compat'

import { showModal } from 'actions/app'
import type { AppState } from 'state/index'
import { useDispatch, useSelector } from 'react-redux'
import SearchField from 'components/search-field/index'
import AddOperator from './add-operator'
import DeleteOperator from './delete-operator'
import colors from 'common/colors'
import {
  Edit as EditIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
export default function Operators() {
  const operatorList = useSelector(
    (state) => (state as AppState).account.operatorsList,
  )
  const dispatch = useDispatch()
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })

  const [columns, setColumns] = useState<any[]>([])

  useEffect(() => {
    const _columns = [
      {
        field: 'name',
        headerName: t('name'),
        flex: 1,
      },
    ]
    setColumns(_columns)
  }, [])

  const [searchValue, setSearchValue] = useState('')

  const filterOperators = (searchValue, operators) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()

      return operators.name?.toLowerCase().indexOf(lowerSearchValue) > -1
    }

    return true
  }

  const editOperator = useCallback(() => {
    if (
      rowSelectionModel.type === 'include' &&
      rowSelectionModel.ids.size === 1
    ) {
      const selectedOperator = find(operatorList, [
        'id',
        rowSelectionModel.ids.values().next().value,
      ])
      dispatch(showModal(<AddOperator selectedOperator={selectedOperator} />))
    }
  }, [rowSelectionModel, operatorList, dispatch])

  const addOperator = useCallback(() => {
    dispatch(showModal(<AddOperator />))
  }, [])

  // Compute filtered operators once to use in both grid and deletion logic
  const filteredOperators = Array.isArray(operatorList)
    ? operatorList
        .map((c) => ({
          id: c.id,
          tenantId: c.tenantId,
          name: c.name,
        }))
        .filter((c) => filterOperators(searchValue, c))
    : []

  const deleteOperator = useCallback(() => {
    if (
      rowSelectionModel.ids.size > 0 ||
      rowSelectionModel.type === 'exclude'
    ) {
      let selectedOperators: typeof filteredOperators = []

      if (rowSelectionModel.type === 'include') {
        // When type is 'include', ids are the selected operators (from visible rows)
        // Single pass over filteredOperators ensures we only get visible and valid operators
        selectedOperators = filteredOperators.filter((op) =>
          rowSelectionModel.ids.has(op.id),
        )
      } else {
        // When type is 'exclude', ids are the unselected operators (hidden rows)
        // So selected operators are all visible rows except those in ids
        const excludedIds = new Set(rowSelectionModel.ids)
        selectedOperators = filteredOperators.filter(
          (op) => !excludedIds.has(op.id),
        )
      }

      // Guard against stale selections or empty results
      if (selectedOperators.length > 0) {
        dispatch(
          showModal(<DeleteOperator selectedOperators={selectedOperators} />),
        )
      }
    }
  }, [rowSelectionModel, filteredOperators, dispatch])

  const onClick = noop

  function CustomNoRowsOverlayOperators() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_operators_found')}
      </div>
    )
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1>{t('operators')}</h1>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <IconButton
            disabled={rowSelectionModel?.ids?.size === 0}
            onClick={deleteOperator}
            title={t('delete_item', { item: t('operator') })}
            size="large"
          >
            <DeleteOutlineOutlinedIcon
              style={{
                color: rowSelectionModel?.ids?.size > 0 ? colors.brand : 'gray',
              }}
            />
          </IconButton>
          <IconButton
            title={t('edit_operator')}
            disabled={rowSelectionModel?.ids?.size === 0}
            onClick={editOperator}
            size="large"
          >
            <EditIcon
              style={{
                color:
                  rowSelectionModel?.ids?.size === 1 ? colors.brand : 'gray',
              }}
            />
          </IconButton>

          <IconButton
            onClick={addOperator}
            title={t('add_operator')}
            size="large"
          >
            <AddIcon />
          </IconButton>
          <SearchField
            sx={{ marginLeft: '10px', minWidth: '200px' }}
            name={searchValue}
            placeholder={t('search')}
            className=""
            onSearch={(value: string) => setSearchValue(value)}
          />
        </Box>
      </Box>
      <div
        style={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          marginTop: '2rem',
        }}
      >
        <DataGrid
          autoHeight
          checkboxSelection
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            setRowSelectionModel(selection)
          }}
          onRowClick={(params) => onClick(params.row)}
          initialState={{
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlayOperators,
          }}
          rows={filteredOperators}
          columns={columns}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </div>
    </Box>
  )
}
